import type { ReactNode } from 'react';
import { Copy } from 'lucide-react';
import { cx } from './ui';
import styles from '../../LootboxPage.module.scss';
import ws from '../WorldsPage.module.scss';

/** Un des deux champs d'une paire FR / EN. */
export interface NameSpec {
  value: string;
  onChange: (v: string) => void;
  error?: string | null;
  hint?: ReactNode;
  placeholder?: string;
  /**
   * Longueur maximale de la valeur retenue. Avec `normalize`, elle porte sur la valeur NORMALISÉE
   * (« République Tchèque », 18 caractères, donne « RepubliqueTcheque », 17) : le champ brut accepte
   * alors plus large et c'est la validation de l'appelant qui tranche.
   */
  maxLength?: number;
  autoFocus?: boolean;
}

/** Plafond du texte brut quand une normalisation raccourcit la saisie (espaces, accents, ponctuation). */
const rawMax = (max: number | undefined, normalized: boolean) => (max == null || !normalized ? max : Math.max(64, max * 2));

function NameInput({ lang, label, spec, disabled, mono, normalize, extra }: {
  lang: 'fr' | 'en';
  label: string;
  spec: NameSpec;
  disabled: boolean;
  mono: boolean;
  normalize?: (v: string) => string;
  extra?: ReactNode;
}) {
  return (
    <div className={ws.namePairCol}>
      <div className={cx(ws.langInput, !!spec.error && ws.langInputBad, disabled && ws.langInputOff)}>
        <span className={cx(ws.langTag, ws[`lang_${lang}`])} aria-hidden>{lang.toUpperCase()}</span>
        <input value={spec.value} disabled={disabled} placeholder={spec.placeholder} maxLength={rawMax(spec.maxLength, !!normalize)}
          autoFocus={spec.autoFocus} spellCheck={!mono} className={mono ? ws.mono : undefined}
          aria-label={`${label} (${lang === 'fr' ? 'français' : 'anglais'})`} aria-invalid={!!spec.error || undefined}
          onChange={(e) => spec.onChange(e.target.value)}
          onBlur={() => {
            if (!normalize) return;
            const n = normalize(spec.value);
            if (n !== spec.value) spec.onChange(n);
          }} />
        {extra}
      </div>
      {spec.error
        ? <span className={ws.fieldError}>{spec.error}</span>
        : spec.hint ? <span className={ws.fieldHint}>{spec.hint}</span> : null}
    </div>
  );
}

/**
 * Paire de noms FR / EN sur deux colonnes (une seule sous 768 px), pastilles de langue. `normalize`
 * s'applique en quittant chaque champ (ex. toIdentifier : « empire afghan » → « EmpireAfghan »).
 * `copyFromFr` : bouton « Reprendre le FR » dans le champ EN tant qu'il est vide et le FR rempli
 * (traduction locale : reprise du FR, normalisée).
 */
export function NamePair({ label, fr, en, disabled = false, normalize, copyFromFr = false, mono = false, required = false, hint }: {
  label: string;
  fr: NameSpec;
  en: NameSpec;
  disabled?: boolean;
  normalize?: (v: string) => string;
  copyFromFr?: boolean;
  /** Police mono (identifiants du jeu : noms fondables, noms d'empire). */
  mono?: boolean;
  /** Astérisque après le libellé. */
  required?: boolean;
  /** Aide commune aux deux champs, sous le libellé (« Optionnel : nom une fois devenu empire »). */
  hint?: ReactNode;
}) {
  const canCopy = copyFromFr && !disabled && !en.value.trim() && !!fr.value.trim();
  return (
    <div className={ws.namePair}>
      <div className={ws.namePairHead}>
        <span className={styles.label}>{label}{required && <span className={ws.req}> *</span>}</span>
        {hint && <span className={ws.fieldHint}>{hint}</span>}
      </div>
      <div className={ws.namePairGrid}>
        <NameInput lang="fr" label={label} spec={fr} disabled={disabled} mono={mono} normalize={normalize} />
        <NameInput lang="en" label={label} spec={en} disabled={disabled} mono={mono} normalize={normalize}
          extra={canCopy && (
            <button type="button" className={ws.langCopy} title="Reprendre le nom FR dans le champ EN"
              onClick={() => en.onChange(normalize ? normalize(fr.value) : fr.value.trim())}>
              <Copy size={12} aria-hidden /> Reprendre le FR
            </button>
          )} />
      </div>
    </div>
  );
}
